import { computed, markRaw, ref, shallowRef } from 'vue';
import { defineStore } from 'pinia';
import { useGroupStore } from './group';
import gtfsHelper from '@/libs/gtfsHelper';
import gtfsIDB from '@/libs/gtfs-indexedDB';
import { useAppStore } from './app';
import {
  AFTER_MIDNIGHT_GRACE_PERIOD,
  SECONDS_IN_DAY,
  computeTripsOfDay,
  isInAfterMidnightWindow,
  previousGtfsDate,
  withRunningPreviousDayTrips,
  type TripOfDay,
} from '@pysae/transit-core/service-day';
import { buildDeviceMaps, computeStopTimesByStops } from '@pysae/transit-core/next-departures';
import type { Shape, Stop, StopTimeData, Trip } from '@/@types/gtfs-ressource';
import type { OnlineDevice } from '@/@types/device';
import { LocationType } from '@/@types/gtfs-ressource';
import type { GtfsAsDict, GtfsFormatted } from '@/@types/gtfs-object';
import { gtfsApi } from '@/api';
import { decorateGtfsForTags, isMockTagsEnabled } from '@/libs/dev/mock-tags';
import { useDevicesStore } from './devices';
import { useTripUpdatesStore } from './trip-updates';
import { reuseUpdatesFor } from '@/libs/next-departures';
import { buildStopClusters, type StopClusterIndex } from '@/libs/helpers/stop-clusters';

interface Points {
  lon: number;
  lat: number;
}

type TripsOfDay = { [tripId: string]: TripOfDay<Trip> };

export interface Bounds {
  north: number | null;
  south: number | null;
  east: number | null;
  west: number | null;
}

export const useGtfsStore = defineStore('gtfs', () => {
  const appStore = useAppStore();
  const groupStore = useGroupStore();
  const deviceStore = useDevicesStore();

  const gtfsData = ref<GtfsFormatted>({
    calendars: {},
    calendarDates: [],
    routes: {},
    stops: {},
    shapes: {},
    trips: {},
  });
  // Shallow: the map reads every coordinate of these shapes, which must not be made
  // reactive one by one, and a page always replaces them as a whole.
  const shownShapes = shallowRef<{ [key: string]: Shape }>({});
  const shownStops = shallowRef<{ [key: string]: Stop }>({});
  /**
   * A displayed stop -> the stop_ids carrying its departures: the children of a
   * declared station, or every member of a group of namesakes merged onto it.
   */
  const stations = ref<{ [key: string]: Array<string> }>({});
  const canonicalOf = ref<StopClusterIndex['canonicalOf']>({});
  const networkMapBounds = ref<Bounds>({ north: null, south: null, east: null, west: null });
  const isGtfsLoadingOnError = ref(false);
  async function loadGtfsFromIDB() {
    const groupId = groupStore.group?.group_id;
    appStore.setLoading({ gtfs: true });
    try {
      const gtfsData = await gtfsIDB.getGtfsIDB('gtfsRessources');

      // The network can change while IndexedDB is read: its cache must not be written under the new one.
      if (groupStore.group?.group_id !== groupId) return;

      // Check data & throw an error if any ressource is not valid
      gtfsHelper.isDataFromIndexedDBValid(gtfsData);

      await setGtfsState(gtfsData);
    } finally {
      if (groupStore.group?.group_id === groupId) appStore.setLoading({ gtfs: false });
    }
  }

  async function loadCurrentGtfsFromAPI() {
    const groupId = groupStore.group?.group_id;
    const gtfsId = groupStore.group?.current_file;
    if (!groupId || !gtfsId) return;
    appStore.resetState();
    // The previous group's index must not outlive it: loadFavorites runs even when this
    // load fails, and would rewrite the stored favorites through another network's ids.
    canonicalOf.value = {};
    stations.value = {};
    // Same for the routes: a deep-linked line alert would resolve its tab against them.
    gtfsData.value.routes = {};
    appStore.setLoading({ gtfs: true });
    isGtfsLoadingOnError.value = false;
    try {
      const gtfsResult = await gtfsApi.getAllGtfsResources(groupId, gtfsId);

      // The group can be cleared or switched while the request runs: a stale response must
      // neither overwrite the cache of the group now selected nor raise its error popup.
      if (groupStore.group?.group_id !== groupId || groupStore.group.current_file !== gtfsId) return;

      if (gtfsResult) {
        const gtfsFormated = gtfsHelper.formatFromApiData(gtfsResult);
        await gtfsIDB.setGtfsIDB('gtfsRessources', gtfsFormated);
        localStorage.setItem('gtfsData.gtfsId', gtfsId);
        await setGtfsState(gtfsFormated);
      } else {
        isGtfsLoadingOnError.value = true;
      }
    } finally {
      if (!isGtfsLoadingOnError.value) {
        appStore.setLoading({ gtfs: false });
      }
    }
    appStore.loadFavorites();
  }

  async function setGtfsState(gtfsAsDict: GtfsAsDict) {
    if (groupStore.group) {
      const gtfsFiltered = gtfsHelper.filterAndFormatGtfs(gtfsAsDict, groupStore.group.private_routes);

      if (import.meta.env.DEV && isMockTagsEnabled()) decorateGtfsForTags(gtfsFiltered);

      gtfsData.value.calendarDates = markRaw(gtfsFiltered.calendarDates ?? []);
      gtfsData.value.calendars = Object.freeze(gtfsFiltered.calendars) ?? {};
      gtfsData.value.routes = Object.freeze(gtfsFiltered.routes) ?? {};
      gtfsData.value.trips = Object.freeze(gtfsFiltered.trips) ?? {};
      gtfsData.value.routeStops = Object.freeze(gtfsFiltered.routeStops); // replace gtfsLinks.routeStops
      const clusters = buildStopClusters(gtfsFiltered.stops ?? {});
      // filterAndFormatGtfs keys the terminus on the parent station only: a merged
      // stop must carry it on its canonical id, the one drawn at the terminus zoom.
      const terminus: { [stopId: string]: boolean } = {};
      Object.keys(gtfsFiltered.terminus ?? {}).forEach(stopId => {
        terminus[clusters.canonicalOf[stopId] ?? stopId] = true;
      });
      gtfsData.value.terminus = Object.freeze(terminus);
      if (gtfsFiltered.stops) setStops(gtfsFiltered.stops, clusters);
      if (gtfsFiltered.shapes) setShapes(gtfsFiltered.shapes);
    }
  }

  function setStops(stops: { [key: string]: Stop }, clusters: StopClusterIndex) {
    const stationDict: { [key: string]: Array<string> } = {};
    Object.entries(clusters.members).forEach(([canonicalId, memberIds]) => {
      // A declared station has no stop_times of its own: only its children are listed.
      const servedIds = memberIds.filter(stopId => stops[stopId]?.location_type !== LocationType.STATION);
      if (servedIds.length > 1 || (servedIds.length === 1 && servedIds[0] !== canonicalId)) {
        stationDict[canonicalId] = servedIds;
      }
    });

    stations.value = Object.freeze(stationDict);
    canonicalOf.value = Object.freeze(clusters.canonicalOf);
    gtfsData.value.stops = Object.freeze(stops);
  }

  /** The id a stop is displayed under: its station, or the namesake it is merged onto. */
  function getCanonicalStopId(stopId: string): string {
    return canonicalOf.value[stopId] ?? stopId;
  }

  function setShapes(shapes: { [key: string]: Shape }) {
    gtfsData.value.shapes = Object.freeze(shapes);
    const bounds: Bounds = {
      north: null,
      south: null,
      east: null,
      west: null,
    };

    function extend(point: Points) {
      if (!bounds.south || point.lat < bounds.south) bounds.south = point.lat;
      if (!bounds.north || point.lat > bounds.north) bounds.north = point.lat;
      if (!bounds.west || point.lon < bounds.west) bounds.west = point.lon;
      if (!bounds.east || point.lon > bounds.east) bounds.east = point.lon;
    }

    for (const shapeId in gtfsData.value.shapes) {
      gtfsData.value.shapes[shapeId].geometry.coordinates.forEach(coordinate => {
        extend({
          lon: coordinate[0],
          lat: coordinate[1],
        });
      });
    }

    if (bounds.north || bounds.south) {
      networkMapBounds.value = bounds;
    }
  }

  function setShownShapes(shapes: { [key: string]: Shape }) {
    shownShapes.value = shapes;
  }

  function setShownStops(stops: { [key: string]: Stop }) {
    shownStops.value = stops;
  }

  const groupTz = computed<string>(() => groupStore.group?.tz || 'Europe/Paris');
  // The lib reads arrays, the GTFS state keys them by id.
  const tripsList = computed<Array<Trip>>(() => Object.values(gtfsData.value.trips ?? {}));
  const calendarsList = computed(() => Object.values(gtfsData.value.calendars ?? {}));

  // getTripsOfDay is read from computeds refreshed on every clock tick and vehicle poll:
  // each day is computed once per loaded GTFS. Its sources are read on every call, so
  // that a caller still tracks them on a cache hit and recomputes on a new GTFS.
  // Today, the day before (journeys over midnight) and a day browsed in a timetable: the
  // least recently used day goes beyond that, or every day ever browsed would stay in memory.
  const TRIPS_OF_DAY_CACHE_SIZE = 4;
  const tripsOfDayCache = new Map<string, TripsOfDay>();
  let tripsOfDayCacheSources: Array<unknown> = [];

  function getTripsOfDay(dateGtfs: string): TripsOfDay {
    const sources = [tripsList.value, calendarsList.value, gtfsData.value.calendarDates, groupTz.value];
    if (sources.some((source, index) => source !== tripsOfDayCacheSources[index])) {
      tripsOfDayCache.clear();
      tripsOfDayCacheSources = sources;
    }
    const tripsOfDay = tripsOfDayCache.get(dateGtfs) ?? freezeTripsOfDay(dateGtfs);
    // Re-inserted on every read: the Map's first key is then the least recently used day.
    tripsOfDayCache.delete(dateGtfs);
    tripsOfDayCache.set(dateGtfs, tripsOfDay);
    if (tripsOfDayCache.size > TRIPS_OF_DAY_CACHE_SIZE) {
      tripsOfDayCache.delete(tripsOfDayCache.keys().next().value!);
    }
    return tripsOfDay;
  }

  // Shared by every caller through the cache: the lib returns mutable objects.
  function freezeTripsOfDay(dateGtfs: string): TripsOfDay {
    const tripsOfDay = computeTripsOfDay(
      { trips: tripsList.value, calendars: calendarsList.value, calendarDates: gtfsData.value.calendarDates },
      dateGtfs,
      groupTz.value,
    );
    Object.values(tripsOfDay).forEach(trip => Object.freeze(trip));
    return Object.freeze(tripsOfDay);
  }

  /**
   * The trips running at `nowTs`: those of today, plus, in the early hours, those of
   * the day before still running past midnight (until an hour after their terminus).
   * Composed on the per-day cache rather than computeOperationalTrips, which would
   * recompute both days and hand out new trip objects on every clock tick.
   */
  function operationalTrips(nowTs: number = Date.now() / 1000): TripsOfDay {
    const today = appStore.todayDateGtfs;
    const todayTripsOfDay = getTripsOfDay(today);
    if (!isInAfterMidnightWindow(today, nowTs, groupTz.value)) return todayTripsOfDay;

    const trips = { ...todayTripsOfDay };
    Object.values(getTripsOfDay(previousGtfsDate(today))).forEach(trip => {
      const lastStopTime = trip.stop_times[trip.stop_times.length - 1];
      if (!lastStopTime || lastStopTime.departure_time <= SECONDS_IN_DAY) return;
      if (trip.midnight + lastStopTime.departure_time + AFTER_MIDNIGHT_GRACE_PERIOD <= nowTs) return;
      trips[trip.trip_id] = trip;
    });
    return trips;
  }

  /**
   * The trips running at `nowTs`, as the departures and the trip detail page show them: the
   * operational trips, where a trip an online vehicle still runs after midnight takes its
   * occurrence of the day before (#302, pysae/transit-core#11).
   */
  function runningTrips(nowTs: number = Date.now() / 1000): TripsOfDay {
    const operational = operationalTrips(nowTs);
    const running = withRunningPreviousDayTrips({
      tripsOfDay: operational,
      data: {
        trips: tripsList.value,
        calendars: calendarsList.value,
        calendarDates: gtfsData.value.calendarDates,
      },
      vehicles: deviceStore.onlineDevices,
      todayGtfs: appStore.todayDateGtfs,
      nowTs,
      tz: groupTz.value,
    });
    if (running === operational) return operational;
    // The lib computes the day before afresh on each call: take the frozen trip of the per-day cache
    // instead, the same object from tick to tick, as the pages watch it.
    const trips = { ...running };
    Object.entries(running).forEach(([tripId, trip]) => {
      if (trip !== operational[tripId]) trips[tripId] = getTripsOfDay(trip.service_date)[tripId] ?? trip;
    });
    return trips;
  }

  /**
   * The online vehicles running the occurrence of `tripId` in `runningTrips`, by the service day
   * rule of the lib (#303): a vehicle of another service day runs another occurrence.
   */
  function devicesOnTrip(tripId: string, nowTs: number = Date.now() / 1000): Array<OnlineDevice> {
    const tripsOfDay = runningTrips(nowTs);
    return deviceStore.onlineDevices.filter(
      device =>
        device.trip_id === tripId &&
        buildDeviceMaps({ devices: [device], tripsOfDay, nowTs }).deviceOnTrip[tripId] === device,
    );
  }

  function todayTrips(): { [key: string]: Trip } {
    return getTripsOfDay(appStore.todayDateGtfs);
  }

  const getRouteEndService = computed<(routeId: string) => number>(() => (routeId: string): number => {
    const todayTripsList = todayTrips();
    if (routeId && todayTripsList && Object.values(todayTripsList).length > 0) {
      const todayTrips = Object.values(todayTripsList).filter(trip => trip?.route_id === routeId);
      if (todayTrips.length === 0) return 0;
      const ordered = todayTrips.sort((a, b) => {
        return (
          a.stop_times[a.stop_times.length - 1].departure_time -
          b.stop_times[b.stop_times.length - 1].departure_time
        );
      });
      const lastTrip = ordered[ordered.length - 1];
      return lastTrip.stop_times[lastTrip.stop_times.length - 1].arrival_time + lastTrip.midnight;
    }
    return 0;
  });

  const getRouteTripsOfDay = computed<(routeId: string) => Array<Trip>>(
    () =>
      (routeId: string): Array<Trip> => {
        const tripsOfTheDay = todayTrips();
        return tripsOfTheDay ? Object.values(tripsOfTheDay).filter(trip => trip?.route_id === routeId) : [];
      },
  );

  const stopsMapped = computed<Map<string, Stop>>(() => {
    const mapObject = new Map<Stop['stop_id'], Stop>();
    if (gtfsData.value.stops) {
      Object.values(gtfsData.value.stops).forEach(stop => {
        mapObject.set(stop.stop_id, stop);
      });
    }
    return mapObject;
  });

  /**
   * The stops served by `routeIds`, each with its canonical stop: what the map
   * draws around a route, rather than the whole network.
   */
  const getRoutesStops = computed<(routeIds: Array<string>) => { [stopId: string]: Stop }>(
    () =>
      (routeIds: Array<string>): { [stopId: string]: Stop } => {
        const routesStops: { [stopId: string]: Stop } = {};
        routeIds.forEach(routeId => {
          Object.keys(gtfsData.value.routeStops?.[routeId] ?? {}).forEach(stopId => {
            const stop = gtfsData.value.stops[stopId];
            if (!stop) return;
            routesStops[stopId] = stop;
            const canonical = gtfsData.value.stops[getCanonicalStopId(stopId)];
            if (canonical) routesStops[canonical.stop_id] = canonical;
          });
        });
        return routesStops;
      },
  );

  /**
   * The two ends of a route, taken from its longest journey of the day: the
   * variant covering the widest portion of the itinerary, as the drawn shape does.
   *
   * Each end is collapsed onto its canonical stop, like `orderRouteStopsByJourney`
   * does: below zoom 16 the map only draws canonical stops, so an end left on its
   * quay would lose its pin and its name there.
   */
  const getRouteEndStopIds = computed<(routeId: string) => Array<string>>(
    () =>
      (routeId: string): Array<string> => {
        const tripsOfDay = getRouteTripsOfDay.value(routeId);
        // The whole-network scan only happens on a route with no service today.
        const fallbackTrips =
          tripsOfDay.length > 0
            ? []
            : Object.values(gtfsData.value.trips ?? {}).filter(trip => trip.route_id === routeId);
        const longest = gtfsHelper.representativeTrip(tripsOfDay, fallbackTrips);
        if (!longest || longest.stop_times.length === 0) return [];
        const ordered = [...longest.stop_times].sort((a, b) => a.stop_sequence - b.stop_sequence);
        // A loop route comes back to where it left: one end, not two.
        return [
          ...new Set([
            getCanonicalStopId(ordered[0].stop_id),
            getCanonicalStopId(ordered[ordered.length - 1].stop_id),
          ]),
        ];
      },
  );

  /** The routes serving any of `stopIds`. */
  const getStopsRouteIds = computed<(stopIds: Array<string>) => Array<string>>(
    () =>
      (stopIds: Array<string>): Array<string> =>
        Object.entries(gtfsData.value.routeStops ?? {})
          .filter(([, routeStops]) => stopIds.some(stopId => routeStops[stopId]))
          .map(([routeId]) => routeId),
  );

  /**
   * Stop times of `stopIds`, flat in the order of `stopIds`, each sorted by departure and
   * not filtered: every page applies its own filter.
   */
  const getStopTimes = computed<(stopIds: Array<string>, dateGtfs?: string | null) => StopTimeData[]>(
    () =>
      (stopIds: Array<string>, dateGtfs: string | null = null): StopTimeData[] => {
        const isToday = !dateGtfs || dateGtfs === appStore.todayDateGtfs;
        // Read on each call, not from useClock: the pages keep this computation off the clock tick.
        const nowTs = Date.now() / 1000;
        // D4 (#293): after midnight, the night trips of the day before on the path of today only,
        // not on a timetable day, even today's (D5).
        const devices = isToday ? deviceStore.onlineDevices : [];
        // #302: same vehicles and nowTs as the lib below.
        const tripsOfDay = dateGtfs ? getTripsOfDay(dateGtfs) : runningTrips(nowTs);

        const stopTimesByStops = computeStopTimesByStops({
          tripsOfDay,
          stopIds,
          devices,
          stopsMapped: stopsMapped.value,
          // D3 (#293): REUSE stops on the path of today only, not on a timetable day (D5).
          // The store is read here, not at setup: it calls useI18n, the app has created it by then.
          tripUpdates: dateGtfs
            ? undefined
            : reuseUpdatesFor(useTripUpdatesStore().tripUpdates ?? [], tripsOfDay),
          nowTs,
        });

        // D1 (#293): info marks every stop time of the trip a vehicle carries, the lib only some.
        // Same nowTs as the lib, so a start_date contradicted by the timetable is ignored alike.
        const { deviceOnTrip } = buildDeviceMaps({ devices, tripsOfDay, nowTs });
        return stopIds.reduce((acc, stopId) => {
          (stopTimesByStops[stopId] ?? []).forEach(stopTime => {
            const device = deviceOnTrip[stopTime.trip_id];
            acc.push(device ? { ...stopTime, device_id: device.device_id } : stopTime);
          });
          return acc;
        }, [] as StopTimeData[]);
      },
  );

  return {
    gtfsData,
    getRouteEndService,
    getRouteTripsOfDay,
    getRouteEndStopIds,
    getRoutesStops,
    getStopsRouteIds,
    getStopTimes,
    isGtfsLoadingOnError,
    networkMapBounds,
    shownStops,
    shownShapes,
    stations,
    canonicalOf,
    getCanonicalStopId,
    loadGtfsFromIDB,
    loadCurrentGtfsFromAPI,
    setShownShapes,
    setShownStops,
    getTripsOfDay,
    operationalTrips,
    runningTrips,
    devicesOnTrip,
    todayTrips,
  };
});
